import {AudioSource} from '../../model/AudioSource';
import {logUtishaDebug, logUtishaError} from 'src/app/util/log';
import {SessionStore} from '../../model/SessionStore';

/**
 * Class for playing a list of MP3 files through an AudioSource
 * Takes a list of MP3 URLs and creates an AudioSource that can be played
 */
export class MusicPlayer {
  readonly DEFAULT_VOLUME = 0.5;
  private _audioSource: AudioSource;
  private _currentIndex = 0;
  private _mp3List: string[] = [];
  private _name: string;
  private _sessionStore: SessionStore | undefined;

  /**
   * Creates a new MusicPlayer for playing a list of MP3 files
   * @param mp3List Array of URLs to MP3 files
   * @param sessionStore The session store for reading/writing music state
   * @param name Optional name for the audio source
   */
  constructor(mp3List: string[], sessionStore: SessionStore | undefined, name = 'Background Music') {
    this._mp3List = mp3List;
    this._name = name;
    this._sessionStore = sessionStore;

    if (mp3List.length === 0) {
      throw new Error('MP3 list cannot be empty');
    }

    // Get the volume from session store if available, otherwise use default
    const initialVolume = sessionStore?.snapshot?.media?.musicVolume || this.DEFAULT_VOLUME;

    // Create initial audio source with first track and proper gain
    this._audioSource = new AudioSource(this._mp3List[0], this._name, initialVolume);

    // Set up callback for when track ends
    this._audioSource.onEnded = () => this.playNextTrack();

    // Update session store with initial track
    this.updateSessionStoreWithTrackInfo(this._mp3List[0]);

    logUtishaDebug(`MusicPlayer created with ${mp3List.length} tracks`);
  }

  /**
   * Gets the AudioSource for playing the music
   */
  get audioSource(): AudioSource {
    return this._audioSource;
  }

  /**
   * Gets the list of MP3 URLs
   */
  get mp3List(): string[] {
    return [...this._mp3List];
  }

  /**
   * Gets the current track index
   */
  get currentIndex(): number {
    return this._currentIndex;
  }

  /**
   * Gets the name of the current audio source
   */
  get name(): string {
    return this._name;
  }

  /**
   * Sets the name of the audio source
   */
  set name(value: string) {
    this._name = value;
  }

  /**
   * Play the next track in the list
   * Automatically loops back to the first track after reaching the end
   * Reuses the same AudioSource instance to avoid reconnection issues
   */
  playNextTrack(): void {
    try {
      // Stop the current track first
      this.stop();

      // Move to next track, looping back to beginning if needed
      this._currentIndex = (this._currentIndex + 1) % this._mp3List.length;

      // Get the URL for the next track
      const newUrl = this._mp3List[this._currentIndex];
      logUtishaDebug(`MusicPlayer switching to track ${this._currentIndex}: ${newUrl}`);

      // Update the existing audio element src instead of creating a new AudioSource
      this._audioSource.src = newUrl;

      // Reset gain (loading a new source can reset the volume)
      this._audioSource.gain = this._sessionStore?.snapshot?.media?.musicVolume || this.DEFAULT_VOLUME;

      // Update the session store with the current track info
      this.updateSessionStoreWithTrackInfo(newUrl);
      this._audioSource.play();
    } catch (error) {
      logUtishaError(`Error playing next track: ${error}`);
    }
  }

  /**
   * Play the previous track in the list
   * Automatically loops to the last track if at the beginning
   * Reuses the same AudioSource instance to avoid reconnection issues
   */
  playPreviousTrack(): void {
    try {
      // Ensure current track is fully stopped
      this.stop();

      // Wait a brief moment to ensure resources are freed
      setTimeout(() => {
        // Move to previous track, looping to end if needed
        this._currentIndex = (this._currentIndex - 1 + this._mp3List.length) % this._mp3List.length;

        // Get the URL for the previous track
        const newUrl = this._mp3List[this._currentIndex];
        logUtishaDebug(`MusicPlayer switching to track ${this._currentIndex}: ${newUrl}`);

        // Update the existing audio element src instead of creating a new AudioSource
        this._audioSource.audioElement.src = newUrl;

        // Update the audio source ID to match the new URL
        this._audioSource._src = newUrl;

        // Update the session store with the current track info
        this.updateSessionStoreWithTrackInfo(newUrl);

        // Load and play the new track
        this._audioSource.audioElement.load();
        this._audioSource.play();
      }, 100); // Small delay to ensure resources are properly released
    } catch (error) {
      logUtishaError(`Error playing previous track: ${error}`);
    }
  }

  /**
   * Start playing the music
   */
  async play(): Promise<void> {
    await this._audioSource.play();
    // Update the session store with the current track info
    this.updateSessionStoreWithTrackInfo(this._mp3List[this._currentIndex]);
  }

  /**
   * Stop playing the music
   */
  stop(): void {
    try {
      // Pause the audio element
      if (this._audioSource && this._audioSource.audioElement) {
        this._audioSource.audioElement.pause();
        this._audioSource.audioElement.currentTime = 0;
      }
    } catch (error) {
      logUtishaError(`Error stopping music: ${error}`);
    }
  }

  /**
   * Pause the music playback
   */
  pause(): void {
    try {
      if (this._audioSource) {
        this._audioSource.pause();
        logUtishaDebug(`Music player paused`);
      }
    } catch (error) {
      logUtishaError(`Error pausing music: ${error}`);
    }
  }

  /**
   * Resume the music playback from the current position
   */
  resume(): void {
    try {
      if (this._audioSource) {
        this._audioSource.resume();
        logUtishaDebug(`Music player resumed`);
      }
    } catch (error) {
      logUtishaError(`Error resuming music: ${error}`);
    }
  }

  /**
   * Set the gain (volume) for the current music
   * @param volume Value from 0.0 (silent) to 1.0 (full volume)
   */
  setVolume(volume: number): void {
    logUtishaDebug(`Set music player volume to ${volume}`);
    if (this._audioSource) {
      this._audioSource.gain = volume;
    }
  }

  /**
   * Get the current gain (volume) value
   * @returns The current gain value from 0.0 to 1.0
   */
  getVolume(): number {
    return this._audioSource ? this._audioSource.gain : 0;
  }

  /**
   * Updates the session store with current track information
   * This is called whenever the track changes
   * @param currentTrack The URL of the current track
   */
  private updateSessionStoreWithTrackInfo(currentTrack: string): void {
    if (this._sessionStore) {
      const currentState = this._sessionStore.snapshot?.media?.musicPlayback || {};
      this._sessionStore.update({
        media: {
          musicPlayback: {
            ...currentState,
            currentTrack,
            started: true,
            paused: false,
            volume: this._sessionStore.snapshot?.media?.musicVolume || this.DEFAULT_VOLUME,
            mp3List: this._mp3List,
          }
        }
      });

      logUtishaDebug(`Updated session store with current track: ${currentTrack} and volume: ${this._sessionStore.snapshot?.media?.musicVolume}`);
    } else {
      logUtishaDebug(`Cannot update session store: sessionStore not available`);
    }
  }

  /**
   * Set the track by index
   * @param index Index of the track to play
   */
  setTrack(index: number): void {
    if (index < 0 || index >= this._mp3List.length) {
      logUtishaError(`Invalid track index: ${index}`);
      return;
    }

    try {
      this._currentIndex = index;
      const newUrl = this._mp3List[this._currentIndex];

      // Store the current gain to maintain volume between tracks
      const currentGain = this._audioSource.gain;

      // Update the existing audio element src instead of creating a new AudioSource
      this._audioSource.audioElement.src = newUrl;

      // Reset gain (loading a new source can reset the volume)
      this._audioSource.gain = currentGain;

      // Update the audio source ID to match the new URL
      this._audioSource._src = newUrl;

      // Update the session store with the current track info
      this.updateSessionStoreWithTrackInfo(newUrl);

      // Load and play the new track
      this._audioSource.audioElement.load();
      this._audioSource.play();
    } catch (error) {
      logUtishaError(`Error setting track to index ${index}: ${error}`);
    }
  }
}
